import React, { useState, useEffect } from 'react';
import { Thermometer, AlertTriangle } from 'lucide-react';

interface TemperatureRange {
  min: number;
  max: number;
  amount: number;
}

const TEMPERATURE_RANGES: TemperatureRange[] = [
  { min: 35, max: Infinity, amount: 1 },
  { min: 30, max: 35, amount: 2 },
  { min: 29, max: 30, amount: 3 },
  { min: 28, max: 29, amount: 4 },
  { min: 25, max: 28, amount: 5 },
  { min: 24, max: 25, amount: 6 },
  { min: 23, max: 24, amount: 7 },
  { min: 22, max: 23, amount: 8 },
  { min: 20, max: 22, amount: 9 },
  { min: 19, max: 20, amount: 10 },
  { min: 18, max: 19, amount: 11 },
  { min: 17, max: 18, amount: 12 },
  { min: 16, max: 17, amount: 13 },
  { min: 15, max: 16, amount: 14 }
];

export function SlowFermentationCalculator() {
  const [temperature, setTemperature] = useState(20);
  const [unit, setUnit] = useState<'C' | 'F'>('C');
  const [starterAmount, setStarterAmount] = useState(9);
  const [isSmallBatch, setIsSmallBatch] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);

  // Convert between Celsius and Fahrenheit
  const toCelsius = (fahrenheit: number) => (fahrenheit - 32) * 5/9;
  const toFahrenheit = (celsius: number) => (celsius * 9/5) + 32;

  // Get temperature in current unit for display
  const displayTemperature = unit === 'C' ? temperature : toFahrenheit(temperature);

  useEffect(() => {
    // Find the appropriate range for the current temperature
    if (temperature < 15) {
      setStarterAmount(0);
      setWarning('Temperature is too low for this fermentation process to work effectively. Consider using a warmer location or a different fermentation method.');
      return;
    }

    const range = TEMPERATURE_RANGES.find(r => temperature >= r.min && temperature < r.max);
    if (range) {
      // Add 1g/kg for small batches
      setStarterAmount(isSmallBatch ? range.amount + 1 : range.amount);
      
      if (temperature >= 35) {
        setWarning('The temperature is too warm for this fermentation duration. In addition to reducing the amount of starter to 1g/kg, also consider reducing the bulk fermentation duration by removing 2hrs per additional degree above 35°C/95°F');
      } else {
        setWarning(null);
      }
    }
  }, [temperature, isSmallBatch]);

  return (
    <div className="space-y-6 text-white/90">
      <div className="bg-gray-600/60 p-6">
        <div className="space-y-6">
          <div className="flex items-start gap-4 p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
            <div className="flex-shrink-0 mt-1">
              <Thermometer className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-amber-400 mb-2">About this Calculator</h3>
              <p className="text-sm text-white/90">
                This calculator helps you determine the right amount of sourdough starter for a slow fermentation process of 18hrs at room temperature, based on batch size and room temperature. By adjusting the temperature slider, you can see the recommended starter amount per kilogram of flour, ensuring a balanced fermentation speed.
              </p>
            </div>
          </div>

          {/* Temperature Unit Selection */}
           <div className="flex items-center gap-4">
                <div className="inline-flex rounded-xl overflow-hidden border border-white/20">
                  <button
                    onClick={() => setUnit('C')}
                    className={`px-4 py-2 text-sm ${
                      unit === 'C' 
                        ? 'bg-amber-500 text-white' 
                        : 'text-white/70 hover:bg-white/10'
                    }`}
                  >
                    Celsius
                  </button>
                  <button
                    onClick={() => setUnit('F')}
                    className={`px-4 py-2 text-sm ${
                      unit === 'F' 
                        ? 'bg-amber-500 text-white' 
                        : 'text-white/70 hover:bg-white/10'
                    }`}
                  >
                    Fahrenheit
                  </button>
                </div>
              </div>
                    
          {/* Batch Size Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              1. Select your Batch Size
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setIsSmallBatch(true)}
                className={`p-4 border rounded-xl transition-colors ${
                  isSmallBatch
                    ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <span className="block font-medium text-white">Small Batch</span>
                <span className="block text-sm text-white/70 mt-1">Less than 2kg flour</span>
              </button>
              <button
                onClick={() => setIsSmallBatch(false)}
                className={`p-4 border rounded-xl transition-colors ${
                  !isSmallBatch
                    ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <span className="block font-medium text-white">Large Batch</span>
                <span className="block text-sm text-white/70 mt-1">2kg flour or more</span>
              </button>
            </div>
          </div>

          {/* Temperature Input */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-white/90">
                2. Set your Room Temperature
              </label>
              <div className="flex items-center gap-2 flex-grow justify-start pl-4">
                  <Thermometer className="w-4 h-4 text-amber-400" />
                  <span className="text-lg font-medium text-amber-400">
                    {Math.round(displayTemperature * 10) / 10}°{unit}
                  </span>
              </div>
             
            </div>

            {/* Temperature Slider */}
            <div>
              <input
                type="range"
                min={unit === 'C' ? 10 : 50}
                max={unit === 'C' ? 40 : 104}
                step="0.1"
                value={displayTemperature}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setTemperature(unit === 'C' ? value : toCelsius(value));
                }}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-runnable-track]:rounded-lg [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/50 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:-mt-1 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-400 [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-white/50 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-sm [&::-moz-range-progress]:bg-amber-400 [&::-moz-range-progress]:rounded-l-lg [&::-moz-range-progress]:h-2"
                style={{
                  background: `linear-gradient(to right, rgb(251 191 36) 0%, rgb(251 191 36) ${
                    unit === 'C'
                      ? (displayTemperature - 10) * 100 / 30
                      : (displayTemperature - 50) * 100 / 54
                  }%, rgba(255, 255, 255, 0.1) ${
                    unit === 'C'
                      ? (displayTemperature - 10) * 100 / 30
                      : (displayTemperature - 50) * 100 / 54
                  }%, rgba(255, 255, 255, 0.1) 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-white/50 mt-1">
                <span>{unit === 'C' ? '10°C' : '50°F'}</span>
                <span>{unit === 'C' ? '40°C' : '104°F'}</span>
              </div>
            </div>

            {/* Results */}
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <h4 className="text-sm font-medium text-white mb-2">Your Recommended Starter Amount</h4>
              {temperature < 15 ? (
                <div className="flex items-start gap-2 text-red-200">
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{warning}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-2xl text-amber-400 font-brand font-light italic">
                    {starterAmount} grams
                  </p>
                  <p className="text-sm text-white/80 mb-2">
                    per kilogram of flour
                  </p>
                  {warning && (
                    <div className="flex items-start gap-2 mt-4 p-3 bg-amber-500/10 backdrop-blur-sm rounded-lg border border-amber-500/20">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
                      <p className="text-sm text-amber-200">{warning}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}